import { useCallback } from 'react';

import { AxiosResponse, isAxiosError } from 'axios';

import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useUiTranslation from 'src/hooks/useUiTranslation';

const useHandleHttpRequestError = () => {
  const { getErrorMessage } = useGetGalenErrorMsg();
  const { tCommon } = useUiTranslation();

  const extractErrorMessage = useCallback((data: string): string => {
    const errorMessageParts = data.split(' ');
    return errorMessageParts.length > 1 ? errorMessageParts[1] : data;
  }, []);

  const handleErrorResponse = useCallback(
    (response: AxiosResponse): string => {
      const { data } = response;
      return getErrorMessage(extractErrorMessage(data));
    },
    [extractErrorMessage, getErrorMessage],
  );

  const handleHttpRequestError = useCallback(
    (err: unknown): string => {
      if (!isAxiosError(err)) {
        if (err instanceof Error) {
          return `${tCommon('bootbox.title.Error')}: ${err.message}`;
        }
        return `${err}`;
      }

      const { response, request, message } = err;

      if (response) {
        return handleErrorResponse(response);
      }

      if (request) {
        // Request was made but no response was received
        return tCommon('message.error.networkError');
      }

      // Something happened in setting up the request that triggered an Error
      return `${tCommon('bootbox.title.Error')}: ${message}`;
    },
    [handleErrorResponse, tCommon],
  );

  return {
    handleHttpRequestError,
  };
};

export default useHandleHttpRequestError;
